import type { AdminShareDecision } from '@pulsify/shared/dto'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useSupabaseSession } from '~/entities/session/model/use-auth'
import { type ShareLinkOwner, shareLinksApi } from '~/entities/share-link/api/share-links'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { log } from '~/shared/lib/logger'
import { QueryKey, queryKey } from '~/shared/lib/query-keys'
import { storageManager } from '~/shared/lib/storage-manager'

const MINUTE = 60_000

export function useShareLinks(businessId: string, target: ShareLinkOwner) {
  return useQuery({
    queryKey: queryKey(QueryKey.SHARE_LINKS, target.kind, target.id),
    queryFn: () => shareLinksApi.forTarget(businessId, target),
    enabled: !!businessId && !!target.id,
    staleTime: MINUTE,
  })
}

export function useCashback(businessId: string) {
  return useQuery({
    queryKey: queryKey(QueryKey.CREDITS_CASHBACK, businessId),
    queryFn: () => shareLinksApi.cashback(businessId),
    enabled: !!businessId,
    staleTime: MINUTE,
  })
}

export function useAdminShareReview() {
  const session = useSupabaseSession()
  return useQuery({
    queryKey: queryKey(QueryKey.ADMIN_SHARE_CASHBACK),
    queryFn: () => shareLinksApi.adminReview(),
    enabled: !!session && storageManager.isAdmin(),
    refetchInterval: MINUTE,
  })
}

export function useDecideShareEarning() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { id: string; decision: AdminShareDecision['decision'] }) =>
      shareLinksApi.adminDecide(input.id, input.decision),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKey(QueryKey.ADMIN_SHARE_CASHBACK) })
    },
    onError: (error: unknown) => {
      log.error(error as Error, '[ShareCashback] Decision failed')
      toast.error(getErrorMessageFromError(error))
    },
  })
}
